/**
 * Web Storage access that answers with a value instead of throwing.
 *
 * Reading `window.localStorage` is not a safe property access. A browser with
 * site data blocked, a document in a restricted third-party context, and some
 * embedded webviews raise `SecurityError` on the property itself, before any
 * method is called. Safari private mode and quota exhaustion raise on write,
 * and a browser with storage removed entirely leaves the property null.
 *
 * A caller using storage as a best-effort cache should not have to know which
 * of those applies, and must never let one of them escape into a code path
 * that has nothing to do with storage. So the property read, the method call,
 * and the null case are all handled here.
 *
 * This does not serialise. Callers own their own parse/stringify semantics,
 * which differ (some tolerate malformed values, some discard the key).
 */

export const LOCAL_STORAGE = 'localStorage';
export const SESSION_STORAGE = 'sessionStorage';

/**
 * Resolve a storage area, or null when it is unavailable.
 *
 * The `window[kind]` read is inside the try deliberately: that read is what
 * throws when site data is blocked, so a caller cannot safely hand us the
 * storage object itself.
 *
 * @param {string} kind LOCAL_STORAGE or SESSION_STORAGE
 * @returns {Storage|null}
 */
function resolveStorage(kind) {
    try {
        return window[kind] || null;
    } catch {
        return null;
    }
}

/**
 * Whether a storage area can be reached at all.
 *
 * The getters answer null for both "no such key" and "storage is unusable",
 * which is the right default for a best-effort cache but wrong for a caller
 * whose work is only worth doing if storage is reachable at all. Those
 * callers ask here first.
 *
 * This is not a stand-in for "the write will land". Nothing short of
 * attempting the write answers that, so a caller that needs to know checks
 * what safeStorageSet returned.
 *
 * This reports on reachability, not writability: a reachable area can still
 * reject an individual write on quota. Check the return of safeStorageSet
 * for that.
 *
 * @param {string} [kind] LOCAL_STORAGE (default) or SESSION_STORAGE
 * @returns {boolean}
 */
export function safeStorageAvailable(kind = LOCAL_STORAGE) {
    return resolveStorage(kind) !== null;
}

/**
 * @param {string} key
 * @param {string} [kind] LOCAL_STORAGE (default) or SESSION_STORAGE
 * @returns {string|null} The stored value, or null when absent or unreadable.
 */
export function safeStorageGet(key, kind = LOCAL_STORAGE) {
    const storage = resolveStorage(kind);
    if (!storage) return null;

    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
}

/**
 * @param {string} key
 * @param {string} value
 * @param {string} [kind] LOCAL_STORAGE (default) or SESSION_STORAGE
 * @returns {boolean} Whether the value was stored.
 */
export function safeStorageSet(key, value, kind = LOCAL_STORAGE) {
    const storage = resolveStorage(kind);
    if (!storage) return false;

    try {
        storage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
}

/**
 * @param {string} key
 * @param {string} [kind] LOCAL_STORAGE (default) or SESSION_STORAGE
 * @returns {boolean} Whether the key was removed.
 */
export function safeStorageRemove(key, kind = LOCAL_STORAGE) {
    const storage = resolveStorage(kind);
    if (!storage) return false;

    try {
        storage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}
